import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ChangeDetectorRef
} from '@angular/core';
import {
  SupersetApiService,
  SupersetDatabase
} from '../../shared/services/superset-api/superset-api.service';
import { finalize } from 'rxjs/operators';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { SharedModule } from 'app/shared/shared.module';

@Component({
  selector: 'app-database-selector',
  templateUrl: './database-selector.component.html',
  styleUrls: ['./database-selector.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatSelectModule,
    MatOptionModule,
    NgClass,
    MatFormFieldModule,
    SharedModule
  ]
})
export class DatabaseSelectorComponent implements OnInit {
  databases: SupersetDatabase[] = [];
  selectedDatabase: SupersetDatabase | null = null;
  loading: boolean = false;
  error: string | null = null;

  @Output() databaseSelected = new EventEmitter<{ id: number; name: string }>();

  constructor(
    private supersetApiService: SupersetApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadDatabases();
  }

  /**
   * Load databases from Superset
   */
  loadDatabases(): void {
    this.loading = true;
    this.error = null;

    this.supersetApiService
      .getDatabasesDetailed(false)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: databases => {
          this.databases = databases;

          // Look for xdlmain database first, then selected database, then default to first
          const xdlMainDb = databases.find(db => {
            return db.database_name.toLowerCase() === 'xdl main';
          });

          const selected =
            xdlMainDb || databases.find(db => db.selected) || databases[0];

          if (selected) {
            this.selectedDatabase = selected;
            this.onDatabaseChange(selected.id);
          }
        },
        error: err => {
          console.error('Error loading databases:', err);
          this.error =
            'Could not load databases. Please check your connection.';
        }
      });
  }

  /**
   * Handle database selection change
   */
  onDatabaseChange(databaseId: number): void {
    const selectedDb = this.databases.find(db => db.id === databaseId);

    if (selectedDb) {
      this.selectedDatabase = selectedDb;
      this.databaseSelected.emit({
        id: selectedDb.id,
        name: selectedDb.database_name
      });
    }
  }

  /**
   * Retry loading databases
   */
  retry(): void {
    this.loadDatabases();
  }

  /**
   * Get the formatted database name
   * If the name is too long, it will be truncated with ellipsis
   */
  getFormattedDatabaseName(name: string): string {
    const maxLength = 20;
    if (name && name.length > maxLength) {
      return name.substring(0, maxLength - 3) + '...';
    }
    return name || 'Select Database';
  }

  /**
   * Format the database type for display
   * Makes the backend type more readable
   */
  formatDatabaseType(type: string): string {
    if (!type) return '';

    // Common DB types to format nicely
    const typeMap: { [key: string]: string } = {
      mysql: 'MySQL',
      postgresql: 'PostgreSQL',
      mssql: 'SQL Server',
      oracle: 'Oracle',
      sqlite: 'SQLite',
      snowflake: 'Snowflake',
      redshift: 'Redshift',
      bigquery: 'BigQuery',
      databricks: 'Databricks',
      clickhouse: 'ClickHouse',
      hive: 'Hive',
      presto: 'Presto',
      trino: 'Trino'
    };

    // Return mapped type or capitalize first letter
    return (
      typeMap[type.toLowerCase()] ||
      type.charAt(0).toUpperCase() + type.slice(1)
    );
  }

  /**
   * Force change detection to update the view
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }
}
